import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
	SideBar1,
	CollapseContainer,
	ListOfAgentContainer,
	AgentListContainer,
	AgentListCard,
	AgentData,
	AgentFavorites,
	AgentName,
	AgentWelcome,
	Sidebar1Header,
	AgentWrapper,
	AgentListMainWrapper,
	AgentListCollapsedHeader,
	IconContainer,
	CollapseIconWrapper,
	ExpandIconWrapper,
	HistoryContainer,
	ExploreAgentsContainer,
	InputWrapper,
	InputField,
	SearchIcon
} from './SidebarAgents.styled';
import { Icon } from "../../../../elements";
import { useStore } from "../../../../hooks";
import { observer } from "mobx-react";
import HistoryContent from './HistoryContent';
import {useNavigate} from "react-router-dom";
import AgentAvatarComponent from './AgentAvatarComponent';

const SidebarAgents = observer(React.forwardRef(({ selectedDefault, handleAgentSelect, isCollapsedForDocument = false, ...props }, ref) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [isMobile, setIsMobile] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const { authStore, historyAgentStore } = useStore();
	const { agents } = authStore;
	const navigate = useNavigate();

	const handleSearch = useCallback((value) => {
		setSearchTerm(value);
	}, []);

	const listSorted = useMemo(() => {
		if (!agents || !searchTerm) {
			return agents || [];
		}
		return agents.slice()
			.sort((a, b) => {
				const nameA = a.name.toUpperCase();
				const nameB = b.name.toUpperCase();
				if (nameA < nameB) return -1;
				if (nameA > nameB) return 1;
				return 0;
			})
			.filter(agent => agent.name.toLowerCase().includes(searchTerm.toLowerCase()));
	}, [agents, searchTerm]);

	const hasAgentMatches = useMemo(() => {
		return searchTerm && listSorted.length > 0;
	}, [searchTerm, listSorted]);

	const handleAgentSelection = useCallback((name, uuid) => {
		handleAgentSelect(name, uuid);
		
		const selectedAgent = agents.find(agent => agent.uuid === uuid);
		if (selectedAgent) {
			historyAgentStore.setSelectedAgent(selectedAgent);
			// Always use HTTP method for all agents
				historyAgentStore.fetchConversationsHTTP(uuid, authStore.userId);
		}
	}, [handleAgentSelect, agents, historyAgentStore, authStore.userId]);

	const toggleSidebar = useCallback(() => {
		setIsExpanded(!isExpanded);
	}, [isExpanded]);

	const handleExploreAgentsClick = useCallback(() => {
		navigate('/v2/agentsView');
	}, [navigate]);

	const renderAgentList = useCallback((showDetails = true) => {
		return listSorted.map(option => (
			<AgentListCard
				className={"agentListCard"}
				onClick={() => handleAgentSelection(option.name, option.uuid)}
				key={option.uuid}
				isSelected={historyAgentStore.selectedAgent?.uuid === option.uuid}
			>
				<AgentAvatarComponent 
					photo_url={option.photo_url}
					name={option.name}
					id={option.uuid}
					isExpanded={isExpanded}
				/>
				{showDetails && (
					<>
						<AgentData>
							<AgentName title={option.name}>{option.name}</AgentName>
							<AgentWelcome></AgentWelcome>
						</AgentData>
						<AgentFavorites></AgentFavorites>
					</>
				)}
			</AgentListCard>
		));
	}, [listSorted, handleAgentSelection, historyAgentStore.selectedAgent?.uuid, isExpanded]);

	const handleMediaQueryChange = useCallback(() => {
		const isMobileSize = window.matchMedia('(max-width: 1024px)').matches;
		const isTabletSize = window.matchMedia('(max-width: 792px)').matches;

		setIsMobile(isMobileSize);
		setIsExpanded(!isMobileSize && !isTabletSize);
	}, []);

	useEffect(() => {
		const mobileMediaQuery = window.matchMedia('(max-width: 1024px)');
		const tabletMediaQuery = window.matchMedia('(max-width: 792px)');

		// Set initial values
		handleMediaQueryChange();

		// Add listeners for subsequent changes
		mobileMediaQuery.addEventListener('change', handleMediaQueryChange);
		tabletMediaQuery.addEventListener('change', handleMediaQueryChange);

		// Cleanup
		return () => {
			mobileMediaQuery.removeEventListener('change', handleMediaQueryChange);
			tabletMediaQuery.removeEventListener('change', handleMediaQueryChange);
		};
	}, []);

	useEffect(() => {
		if (selectedDefault && agents?.length > 0) {
			const selectedAgent = agents.find(agent => agent.name === selectedDefault);

			if (selectedAgent) {
				historyAgentStore.setSelectedAgent(selectedAgent);
				// Always use HTTP method for all agents
				historyAgentStore.fetchConversationsHTTP(selectedAgent.uuid || selectedAgent.id, authStore.userId);
			}
		}
	}, [selectedDefault, agents, authStore.userId]);

	useEffect(() => {
		if (historyAgentStore.selectedAgent?.photo_url) {
			const img = new Image();
			img.src = historyAgentStore.selectedAgent.photo_url;
		}
	}, []);

	// Force collapsed state when in document edit mode
	const effectiveExpanded = isCollapsedForDocument ? false : isExpanded;
	
	// Debug logging
	useEffect(() => {
		console.log('🔍 SidebarAgents - isCollapsedForDocument:', isCollapsedForDocument);
		console.log('🔍 SidebarAgents - effectiveExpanded:', effectiveExpanded);
	}, [isCollapsedForDocument, effectiveExpanded]);

	return (
		<>
		<SideBar1 id="styledd" ref={ref} {...props} isExpanded={effectiveExpanded} isMobile={isMobile} $isCollapsedForDocument={isCollapsedForDocument}>
			<CollapseContainer isExpanded={effectiveExpanded} onClick={isCollapsedForDocument ? undefined : toggleSidebar}>
				{effectiveExpanded ? (
					<>
						<CollapseIconWrapper>
							<IconContainer>
								<Icon
									name="collapseBlack"
									height="16px"
									width="16px"
									onClick={toggleSidebar}
								/>
							</IconContainer>
						</CollapseIconWrapper>
						<Sidebar1Header>
							<InputWrapper>
								<InputField
									type="text"
									value={searchTerm}
									onChange={(e) => handleSearch(e.target.value)}
									onClick={e => e.stopPropagation()}
									placeholder="Find chats or agents..."
								/>
								<SearchIcon>
									<Icon name="magnifier" height="40px" width="20px" />
								</SearchIcon>
							</InputWrapper>
						</Sidebar1Header>
					</>
				) : (
					<AgentListMainWrapper>
						<AgentListCollapsedHeader>
							<ExpandIconWrapper>
								<IconContainer>
									<Icon
										name="collapseRight"
										height="16px"
										width="16px"
										onClick={toggleSidebar}
									/>
								</IconContainer>
							</ExpandIconWrapper>
						</AgentListCollapsedHeader>
					</AgentListMainWrapper>
				)}
			</CollapseContainer>
			<AgentWrapper isExpanded={effectiveExpanded}>
				{effectiveExpanded && (
					<>
						<ListOfAgentContainer className="list-agents">
							<AgentListContainer isExpanded={isExpanded}>
								<div className="agent-title">Agents</div>
								{renderAgentList(true)}
							</AgentListContainer>
						</ListOfAgentContainer>
						
						<ExploreAgentsContainer isExpanded={isExpanded} onClick={handleExploreAgentsClick}>
							<span className="explore-agents-text">Explore Agents</span>
							<Icon name={"arrowNorthEast"} height={'13px'} width={'13px'} className="arrow-icon" />
						</ExploreAgentsContainer>
						
						<HistoryContainer isExpanded={isExpanded}>
							<HistoryContent searchTerm={searchTerm} hasAgentMatches={hasAgentMatches} />
						</HistoryContainer>
					</>
				)}
			</AgentWrapper>
		</SideBar1>
	</>	
	);
}));

export default SidebarAgents;